import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaFacebookF, FaHeadphones, FaPhoneAlt, FaWhatsapp} from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { useSettings } from "context/SettingsContext";
import {FcBusinessContact} from "react-icons/fc";
import { t } from "context/translations";

const DEFAULT_PHONE = "+8801910060071";
const WHATSAPP_NUMBER = "+8801910060071";
const WHATSAPP_MESSAGE = "Hi Super Store, I need help with my order.";
const FACEBOOK_URL = "https://www.facebook.com/share/1AoiGtts1W/";

const ContactUsFloatingButton = ({ className = "", showAt = 200 }) => {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const modalRef = useRef(null);
  const { theme, language } = useSettings();
  const isDark = theme === "dark";

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > showAt);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [showAt]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  if (!visible) return null;

  const whatsappHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open contact options"
        className={`
          fixed sm:bottom-6 bottom-16 right-14 sm:right-24
          w-8 h-8 sm:w-12 sm:h-12
          rounded-full shadow-lg
          bg-gradient-to-br from-sky-500 to-cyan-600 text-white
          flex items-center justify-center
          transition-transform duration-200 hover:scale-105 active:scale-95
          z-40
          ${className}
        `}
        >
        <FaHeadphones className="text-lg sm:text-xl" />
      </button>

      {open &&
        createPortal(
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-4 py-6">
            <div
              ref={modalRef}
              className={`
                w-full max-w-sm rounded-[28px] shadow-2xl overflow-hidden
                ${isDark ? "bg-slate-900 text-white" : "bg-white text-slate-900"}
              `}
            >
              <div className="flex items-start justify-between px-6 pt-6">
                <h2 className="text-2xl font-semibold">Contact Us</h2>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close contact modal"
                  className={`
                    flex h-10 w-10 items-center justify-center rounded-full
                    ${isDark ? "bg-white/10 hover:bg-white/15" : "bg-slate-100 hover:bg-slate-200"}
                    transition
                  `}
                >
                  <IoClose className="text-2xl" />
                </button>
              </div>

              <div className="px-6 pt-4 pb-6 text-center">
                <p className="text-sm sm:text-[15px] leading-6 text-slate-500">
                  For any queries regarding products, orders, or delivery, feel
                  free to contact us. You can also reach us through WhatsApp,
                  Facebook, or phone.
                </p>

                <div className="mt-7 flex justify-center">
                  <div
                    className={`
                      flex h-28 w-28 items-center justify-center rounded-full
                      ${isDark ? "bg-white/5" : "bg-slate-50"}
                    `}
                  >
                    <FcBusinessContact className="text-7xl text-orange-400" />
                  </div>
                </div>

                <div className="mt-7 grid grid-cols-2 gap-3">
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noreferrer"
                    className="
                      flex items-center justify-center gap-2 rounded-full border
                      border-emerald-300 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700
                      transition hover:scale-[1.02] hover:bg-emerald-100
                    "
                  >
                    <FaWhatsapp className="text-xl" />
                    WhatsApp
                  </a>

                  <a
                    href={FACEBOOK_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="
                      flex items-center justify-center gap-2 rounded-full border
                      border-blue-300 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-600
                      transition hover:scale-[1.02] hover:bg-blue-100
                    "
                  >
                    <FaFacebookF className="text-lg" />
                    Facebook
                  </a>
                </div>

                <a
                  href={`tel:${DEFAULT_PHONE.replace(/\s+/g, "")}`}
                  className="
                    mt-3 flex items-center justify-center gap-2 rounded-full
                    bg-gradient-to-r from-blue-900 to-blue-500 px-4 py-3 text-sm font-semibold text-white
                    transition hover:scale-[1.01] hover:opacity-95
                  "
                >
                  <FaPhoneAlt className="text-base" />
                  {t("phoneNumber", language)}
                </a>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default ContactUsFloatingButton;
